<button
  #bellButton
  type="button"
  class="notification-bell"
  (click)="togglePanel()"
  [attr.aria-expanded]="open()"
  aria-haspopup="dialog"
  i18n-aria-label="@@notificationBellAria"
  aria-label="Notifications"
>
  <app-svg-icon icon="bell" [size]="20" color="var(--color-grey-600)"></app-svg-icon>
  <span *ngIf="notifications.unreadCount() > 0" class="notification-badge" aria-live="polite">{{
    notifications.unreadCount() > 99 ? '99+' : notifications.unreadCount()
  }}</span>
</button>

<div class="notification-overlay" [class.visible]="open()" (click)="closePanel()" aria-hidden="true"></div>

<div
  class="notification-drawer"
  [class.visible]="open()"
  [attr.aria-hidden]="!open()"
  [attr.inert]="open() ? null : ''"
  (keydown.escape)="closePanel()"
>
  <div class="notification-panel" role="dialog" i18n-aria-label="@@notificationPanelAria" aria-label="Notifications">
    <div class="notification-panel-head">
      <h2 class="notification-heading" i18n="@@notificationPanelTitle">Notifications</h2>
      <span *ngIf="notifications.unreadCount() > 0" class="notification-new-badge">
        {{ notifications.unreadCount() }} <ng-container i18n="@@notificationNewSuffix">new</ng-container>
      </span>

      <button
        type="button"
        class="notification-link-button notification-mark-all"
        [disabled]="notifications.unreadCount() === 0"
        (click)="markAllRead()"
      >
        <app-svg-icon icon="tick-double" [size]="16"></app-svg-icon>
        <span i18n="@@notificationMarkAllRead">Mark all as read</span>
      </button>

      <button
        type="button"
        class="notification-icon-button"
        (click)="closePanel()"
        i18n-aria-label="@@notificationClose"
        aria-label="Close"
      >
        <app-svg-icon icon="cancel" [size]="16"></app-svg-icon>
      </button>
    </div>

    <div class="notification-toolbar">
      <div class="notification-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="notification-tab"
          [class.active]="unreadOnly()"
          [attr.aria-selected]="unreadOnly()"
          (click)="setUnreadOnly(true)"
          i18n="@@notificationTabUnread"
        >
          Unread
        </button>
        <button
          type="button"
          role="tab"
          class="notification-tab"
          [class.active]="!unreadOnly()"
          [attr.aria-selected]="!unreadOnly()"
          (click)="setUnreadOnly(false)"
          i18n="@@notificationTabAll"
        >
          All
        </button>
      </div>

      <div class="notification-toolbar-actions">
        <button
          #filterButton
          type="button"
          class="notification-icon-button"
          [class.active]="selectedCategories().length > 0"
          (click)="toggleFilterMenu()"
          [attr.aria-expanded]="filterOpen()"
          i18n-aria-label="@@notificationFilterAria"
          aria-label="Filter by category"
        >
          <app-svg-icon icon="filter" [size]="18"></app-svg-icon>
        </button>
        <button
          type="button"
          class="notification-icon-button"
          (click)="openSettings()"
          i18n-aria-label="@@notificationSettingsAria"
          aria-label="Notification settings"
        >
          <app-svg-icon icon="settings" [size]="18"></app-svg-icon>
        </button>
      </div>
    </div>

    <dx-popover
      [target]="filterButton"
      [visible]="filterOpen()"
      (visibleChange)="$event ? null : filterOpen.set(false)"
      [wrapperAttr]="{ class: 'gpd-popover notification-filter-popover' }"
      [width]="347"
      position="bottom"
    >
      <div class="notification-filter-menu">
        <p class="notification-filter-hint" i18n="@@notificationFilterHint">
          Please choose which categories you want to see.
        </p>

        <button
          type="button"
          class="notification-filter-option"
          [class.selected]="selectedCategories().length === 0"
          (click)="clearCategories()"
        >
          <app-svg-icon class="notification-filter-icon" icon="grid-view" [size]="16"></app-svg-icon>
          <span class="notification-filter-label" i18n="@@notificationFilterAll">All</span>
          <app-gpd-checkbox
            class="notification-filter-check"
            [value]="selectedCategories().length === 0"
          ></app-gpd-checkbox>
        </button>

        <button
          *ngFor="let category of categories"
          type="button"
          class="notification-filter-option"
          [class.selected]="isCategorySelected(category.key)"
          (click)="toggleCategory(category.key)"
        >
          <app-svg-icon class="notification-filter-icon" [icon]="categoryIcon(category.key)" [size]="16"></app-svg-icon>
          <span class="notification-filter-label">{{ category.label }}</span>
          <app-gpd-checkbox
            class="notification-filter-check"
            [value]="isCategorySelected(category.key)"
          ></app-gpd-checkbox>
        </button>
      </div>
    </dx-popover>

    <div class="notification-list">
      <div *ngIf="loadError()" class="notification-empty notification-error">{{ loadError() }}</div>

      <div *ngIf="!loadError() && !loading() && rows().length === 0" class="notification-empty">
        <span *ngIf="unreadOnly()" i18n="@@notificationEmptyUnread">Nothing unread.</span>
        <span *ngIf="!unreadOnly()" i18n="@@notificationEmptyAll">No notifications yet.</span>
      </div>

      <ng-container *ngFor="let group of groups()">
        <div class="notification-day">{{ group.label }}</div>

        <button
          *ngFor="let row of group.rows"
          type="button"
          class="notification-row"
          [class.unread]="!row.isRead"
          [class.static]="!actionLabel(row)"
          (click)="openNotification(row)"
        >
          <app-user-avatar
            *ngIf="!isSystem(row)"
            class="notification-avatar"
            [userEmail]="row.actorEmail"
            [initials]="initials(row)"
            [size]="32"
          ></app-user-avatar>

          <span class="notification-body">
            <span class="notification-title">{{ row.title }}</span>
            <span *ngIf="row.description" class="notification-subtitle">{{ row.description }}</span>
            <span class="notification-meta">
              <span class="notification-action">{{ actionLabel(row) }}</span>
              <span class="notification-time">{{ relativeTime(row.occurredAt) }}</span>
            </span>
          </span>

          <span class="notification-dot" [class.hidden]="row.isRead" aria-hidden="true"></span>
        </button>
      </ng-container>

      <div *ngIf="loading()" class="notification-empty" i18n="@@notificationLoading">Loading…</div>

      <button
        *ngIf="hasMore() && !loading()"
        type="button"
        class="notification-link-button notification-load-more"
        (click)="loadMore()"
        i18n="@@notificationLoadMore"
      >
        Load more
      </button>
    </div>
  </div>
</div>
